import React, { useState, useEffect, useCallback } from 'react';
import { getMyTimesheet, saveTimesheetEntry, getTimesheetSummary, getStaffTimesheet } from '../api/apiClient';
import './Timesheet.css';

// ── Date utilities ──────────────────────────────────────────────────────────

const pad = n => String(n).padStart(2, '0');
const toDateStr = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const displayDate = s => { const [y, m, d] = s.split('-').map(Number); return `${m}/${d}/${String(y).slice(2)}`; };
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const dayName = s => { const [y, m, d] = s.split('-').map(Number); return DAYS[new Date(y, m - 1, d).getDay()]; };
const isWeekend = s => { const n = dayName(s); return n === 'Sat' || n === 'Sun'; };

const getPayPeriod = (ref = new Date()) => {
  const y = ref.getFullYear(), m = ref.getMonth(), d = ref.getDate();
  return d <= 15
    ? { start: toDateStr(new Date(y, m, 1)),  end: toDateStr(new Date(y, m, 15)) }
    : { start: toDateStr(new Date(y, m, 16)), end: toDateStr(new Date(y, m + 1, 0)) };
};

const shiftPeriod = (period, dir) => {
  const anchor = dir === -1 ? period.start : period.end;
  const d = new Date(anchor + 'T12:00:00');
  d.setDate(d.getDate() + dir);
  return getPayPeriod(d);
};

const getDays = (start, end) => {
  const days = [], cur = new Date(start + 'T12:00:00'), fin = new Date(end + 'T12:00:00');
  while (cur <= fin) { days.push(toDateStr(new Date(cur))); cur.setDate(cur.getDate() + 1); }
  return days;
};

const periodLabel = p => {
  const [, sm, sd] = p.start.split('-').map(Number);
  const [, em, ed] = p.end.split('-').map(Number);
  const year = p.start.split('-')[0];
  return sm === em
    ? `${MONTHS[sm-1]} ${sd}–${ed}, ${year}`
    : `${MONTHS[sm-1]} ${sd} – ${MONTHS[em-1]} ${ed}, ${year}`;
};

const calcHours = (ti, ls, le, to) => {
  if (!ti || !to) return 0;
  const toMin = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  let mins = toMin(to) - toMin(ti);
  if (ls && le) mins -= toMin(le) - toMin(ls);
  return Math.max(0, +(mins / 60).toFixed(2));
};

const fmtHours = h => Number(h).toFixed(2);

const fmt12h = t => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${pad(m)} ${h >= 12 ? 'PM' : 'AM'}`;
};

const downloadCSV = (filename, rows) => {
  const csv = rows.map(r =>
    r.map(cell => {
      const s = String(cell ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')
  ).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
};

const EMPTY = { id: null, time_in: '', lunch_start: '', lunch_end: '', time_out: '', hours_worked: 0 };

// ── Component ────────────────────────────────────────────────────────────────

const Timesheet = ({ currentUser }) => {
  const isAdminUser = currentUser?.isAdmin;
  const [tab, setTab]       = useState('my');
  const [period, setPeriod] = useState(getPayPeriod());
  const days = getDays(period.start, period.end);

  // ── My timesheet ──
  const [rows,     setRows]     = useState({});
  const [modified, setModified] = useState(new Set());
  const [saving,   setSaving]   = useState(new Set());

  const fetchMine = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const res = await getMyTimesheet(currentUser.id, period.start, period.end);
      const map = {};
      res.data.forEach(e => {
        map[e.work_date] = {
          id: e.id,
          time_in:      e.time_in      || '',
          lunch_start:  e.lunch_start  || '',
          lunch_end:    e.lunch_end    || '',
          time_out:     e.time_out     || '',
          hours_worked: e.hours_worked || 0,
        };
      });
      setRows(map);
      setModified(new Set());
    } catch (err) {
      console.error('Error fetching timesheet:', err);
    }
  }, [currentUser?.id, period]);

  useEffect(() => { fetchMine(); }, [fetchMine]);

  const handleChange = (date, field, value) => {
    setRows(prev => {
      const entry = { ...(prev[date] || EMPTY), [field]: value };
      entry.hours_worked = calcHours(entry.time_in, entry.lunch_start, entry.lunch_end, entry.time_out);
      return { ...prev, [date]: entry };
    });
    setModified(prev => new Set(prev).add(date));
  };

  const handleSave = async date => {
    const entry = rows[date] || EMPTY;
    setSaving(prev => new Set(prev).add(date));
    try {
      const res = await saveTimesheetEntry({
        staff_id:        currentUser.id,
        work_date:       date,
        time_in:         entry.time_in      || null,
        lunch_start:     entry.lunch_start  || null,
        lunch_end:       entry.lunch_end    || null,
        time_out:        entry.time_out     || null,
        pay_period_start: period.start,
        pay_period_end:   period.end,
      });
      setRows(prev => ({ ...prev, [date]: { ...prev[date], hours_worked: res.data.hours_worked } }));
      setModified(prev => { const n = new Set(prev); n.delete(date); return n; });
    } catch {
      alert('Failed to save entry. Please try again.');
    } finally {
      setSaving(prev => { const n = new Set(prev); n.delete(date); return n; });
    }
  };

  const totalHours = days.reduce((sum, d) => sum + (rows[d]?.hours_worked || 0), 0);

  // ── Admin summary ──
  const [summary,       setSummary]       = useState([]);
  const [loadingSummary,setLoadingSummary]= useState(false);
  const [expandedId,    setExpandedId]    = useState(null);
  const [staffRows,     setStaffRows]     = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!currentUser?.id) return;
    setLoadingSummary(true);
    try {
      const res = await getTimesheetSummary(currentUser.id, period.start, period.end);
      setSummary(res.data);
    } catch (err) {
      console.error('Error fetching summary:', err);
    } finally {
      setLoadingSummary(false);
    }
  }, [currentUser?.id, period]);

  useEffect(() => {
    if (tab === 'team' && isAdminUser) fetchSummary();
  }, [tab, fetchSummary, isAdminUser]);

  // Reset expanded staff when period changes
  useEffect(() => { setExpandedId(null); setStaffRows([]); }, [period]);

  const handleExpand = async staffId => {
    if (expandedId === staffId) { setExpandedId(null); setStaffRows([]); return; }
    setExpandedId(staffId);
    setLoadingDetail(true);
    try {
      const res = await getStaffTimesheet(currentUser.id, staffId, period.start, period.end);
      setStaffRows(res.data);
    } catch (err) {
      console.error('Error loading staff detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const detailMap = {};
  staffRows.forEach(r => { detailMap[r.work_date] = r; });
  const detailTotalHours = staffRows.reduce((s, r) => s + (r.hours_worked || 0), 0);

  // ── Export helpers ──
  const DETAIL_HEADER = ['Date', 'Day', 'Time In', 'Lunch Start', 'Lunch End', 'Time Out', 'Hours Worked'];

  const exportMyTimesheet = () => {
    const name = (currentUser?.name || 'Employee').replace(/\s+/g, '_');
    const dataRows = days.map(date => {
      const e = rows[date] || EMPTY;
      return [displayDate(date), dayName(date), fmt12h(e.time_in), fmt12h(e.lunch_start), fmt12h(e.lunch_end), fmt12h(e.time_out), fmtHours(e.hours_worked)];
    });
    dataRows.push(['', '', '', '', '', 'Total', fmtHours(totalHours)]);
    downloadCSV(`Timesheet_${name}_${period.start}_${period.end}.csv`, [DETAIL_HEADER, ...dataRows]);
  };

  const exportSummary = () => {
    const header = ['Name', 'Hours Worked', 'Days Logged'];
    const dataRows = summary.map(s => [s.name, fmtHours(s.total_hours), s.entry_count]);
    downloadCSV(`Team_Overview_${period.start}_${period.end}.csv`, [header, ...dataRows]);
  };

  const exportIndividual = () => {
    const staffMember = summary.find(s => s.id === expandedId);
    const name = (staffMember?.name || 'Employee').replace(/\s+/g, '_');
    const dataRows = days
      .filter(date => !isWeekend(date) || detailMap[date])
      .map(date => {
        const e = detailMap[date];
        return [displayDate(date), dayName(date), fmt12h(e?.time_in), fmt12h(e?.lunch_start), fmt12h(e?.lunch_end), fmt12h(e?.time_out), fmtHours(e?.hours_worked || 0)];
      });
    dataRows.push(['', '', '', '', '', 'Total', fmtHours(detailTotalHours)]);
    downloadCSV(`Timesheet_${name}_${period.start}_${period.end}.csv`, [DETAIL_HEADER, ...dataRows]);
  };

  // ── Render ──
  return (
    <div className="timesheet-page">

      {/* Page header */}
      <div className="ts-page-header">
        <h2 className="ts-page-title">Timesheet</h2>
        {isAdminUser && (
          <div className="ts-tabs">
            <button className={`ts-tab ${tab === 'my' ? 'active' : ''}`} onClick={() => setTab('my')}>
              My Timesheet
            </button>
            <button className={`ts-tab ${tab === 'team' ? 'active' : ''}`} onClick={() => setTab('team')}>
              Team Overview
            </button>
          </div>
        )}
      </div>

      {/* Period navigator */}
      <div className="ts-period-bar">
        <button className="ts-nav-btn" onClick={() => setPeriod(p => shiftPeriod(p, -1))}>‹</button>
        <span className="ts-period-label">{periodLabel(period)}</span>
        <button className="ts-nav-btn" onClick={() => setPeriod(p => shiftPeriod(p, 1))}>›</button>
        <button className="ts-current-btn" onClick={() => setPeriod(getPayPeriod())}>Current</button>
      </div>

      {/* ── MY TIMESHEET ─────────────────────────────────────────────────── */}
      {tab === 'my' && (
        <div className="ts-card">
          {/* Totals banner */}
          <div className="ts-totals-bar">
            <div>
              <div className="ts-totals-label">Total Hours Worked</div>
              <div className="ts-totals-value">{fmtHours(totalHours)}</div>
            </div>
            <div className="ts-totals-right">
              <div className="ts-period-dates">
                <span className="ts-period-date-pill">{displayDate(period.start)}</span>
                <span className="ts-period-sep">–</span>
                <span className="ts-period-date-pill">{displayDate(period.end)}</span>
              </div>
              <button className="ts-export-btn" onClick={exportMyTimesheet}>⬇ Export CSV</button>
            </div>
          </div>

          <div className="ts-table-wrap">
            <table className="ts-table">
              <thead>
                <tr>
                  <th className="col-date">Date</th>
                  <th className="col-time">Time In</th>
                  <th className="col-time">Lunch Start</th>
                  <th className="col-time">Lunch End</th>
                  <th className="col-time">Time Out</th>
                  <th className="col-hours">Hours</th>
                  <th className="col-action"></th>
                </tr>
              </thead>
              <tbody>
                {days.map(date => {
                  const entry   = rows[date] || EMPTY;
                  const dirty   = modified.has(date);
                  const isSaving= saving.has(date);
                  const weekend = isWeekend(date);
                  return (
                    <tr key={date} className={weekend ? 'ts-weekend' : ''}>
                      <td className="col-date">
                        <span className="ts-dayname">{dayName(date)}</span>
                        <span className="ts-datestr">{displayDate(date)}</span>
                      </td>
                      {['time_in','lunch_start','lunch_end','time_out'].map(f => (
                        <td key={f} className="col-time">
                          <input
                            type="time"
                            className="ts-time-input"
                            value={entry[f]}
                            onChange={e => handleChange(date, f, e.target.value)}
                          />
                        </td>
                      ))}
                      <td className="col-hours">
                        <span className={entry.hours_worked > 0 ? 'ts-hrs-pos' : 'ts-hrs-zero'}>
                          {fmtHours(entry.hours_worked)}
                        </span>
                      </td>
                      <td className="col-action">
                        {dirty && (
                          <button
                            className="ts-save-btn"
                            onClick={() => handleSave(date)}
                            disabled={isSaving}
                          >
                            {isSaving ? '…' : 'Save'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="ts-tfoot">
                  <td colSpan="5" className="ts-tfoot-label">Total Hours Worked</td>
                  <td className="col-hours ts-tfoot-total">{fmtHours(totalHours)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ── TEAM OVERVIEW (admin) ─────────────────────────────────────────── */}
      {tab === 'team' && isAdminUser && (
        <div className="ts-card">
          <div className="ts-card-header">
            <h3 className="ts-card-title">Hours Summary — {periodLabel(period)}</h3>
            {summary.length > 0 && (
              <button className="ts-export-btn" onClick={exportSummary}>⬇ Export CSV</button>
            )}
          </div>
          {loadingSummary ? (
            <p className="ts-loading">Loading…</p>
          ) : (
            <table className="ts-summary-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="align-right">Hours Worked</th>
                  <th className="align-right">Days Logged</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {summary.map(s => (
                  <React.Fragment key={s.id}>
                    <tr
                      className={`ts-summary-row ${expandedId === s.id ? 'ts-row-open' : ''}`}
                      onClick={() => handleExpand(s.id)}
                    >
                      <td className="ts-staff-name">{s.name}</td>
                      <td className="align-right">
                        <span className={s.total_hours > 0 ? 'ts-hrs-pos' : 'ts-hrs-zero'}>
                          {fmtHours(s.total_hours)}
                        </span>
                      </td>
                      <td className="align-right ts-days-count">{s.entry_count}</td>
                      <td className="ts-chevron">{expandedId === s.id ? '▲' : '▼'}</td>
                    </tr>

                    {expandedId === s.id && (
                      <tr className="ts-detail-row">
                        <td colSpan="4" className="ts-detail-cell">
                          {loadingDetail ? (
                            <p className="ts-loading">Loading…</p>
                          ) : (
                            <>
                            <div className="ts-detail-export-row">
                              <button className="ts-export-btn" onClick={e => { e.stopPropagation(); exportIndividual(); }}>
                                ⬇ Export CSV
                              </button>
                            </div>
                            <table className="ts-detail-table">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Time In</th>
                                  <th>Lunch Start</th>
                                  <th>Lunch End</th>
                                  <th>Time Out</th>
                                  <th className="align-right">Hours</th>
                                </tr>
                              </thead>
                              <tbody>
                                {days.map(date => {
                                  const e = detailMap[date];
                                  if (!e && isWeekend(date)) return null;
                                  const hrs = e?.hours_worked || 0;
                                  return (
                                    <tr key={date} className={isWeekend(date) ? 'ts-weekend' : ''}>
                                      <td>
                                        <span className="ts-dayname">{dayName(date)}</span>{' '}
                                        {displayDate(date)}
                                      </td>
                                      <td>{e?.time_in     || <span className="ts-dash">—</span>}</td>
                                      <td>{e?.lunch_start || <span className="ts-dash">—</span>}</td>
                                      <td>{e?.lunch_end   || <span className="ts-dash">—</span>}</td>
                                      <td>{e?.time_out    || <span className="ts-dash">—</span>}</td>
                                      <td className="align-right">
                                        <span className={hrs > 0 ? 'ts-hrs-pos' : 'ts-hrs-zero'}>
                                          {fmtHours(hrs)}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr className="ts-tfoot">
                                  <td colSpan="5" className="ts-tfoot-label">Total</td>
                                  <td className="align-right ts-tfoot-total">{fmtHours(detailTotalHours)}</td>
                                </tr>
                              </tfoot>
                            </table>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default Timesheet;
